import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById, userBlock, getUserPurchases, deletePurchases, deleteUsers } from "../../api/Calling";
import { toast } from "react-toastify";
import formatDateTime from "../../components/formatDateTime";
import {
  FaUser,
  FaArrowLeft,
  FaTrash,
  FaExclamationTriangle,
  FaBan,
  FaCheckCircle,
  FaShoppingCart,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";

const UserInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [purchasesData, setPurchasesData] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchasesPagination, setPurchasesPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showDeletePurchaseModal, setShowDeletePurchaseModal] = useState(false);
  const [deletePurchaseTarget, setDeletePurchaseTarget] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const fetchUserData = async () => {
    setUserLoading(true);
    try {
      const resp = await getUserById(id);
      if (resp?.status && resp?.data) {
        setUserData(resp.data);
      } else {
        toast.error(resp?.message || "Failed to load user data");
        navigate("/admin/manager/user");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load user data");
      navigate("/admin/manager/user");
    }
    setUserLoading(false);
  };

  const fetchPurchases = async () => {
    setPurchasesLoading(true);
    try {
      const params = `?page=${purchasesPagination.page}&limit=${purchasesPagination.limit}`;
      const resp = await getUserPurchases(id, params);

      if (resp?.status && Array.isArray(resp.data)) {
        setPurchasesData(resp.data);
        setPurchasesPagination({
          page: resp.currentPage,
          limit: purchasesPagination.limit,
          total: resp.total,
          totalPages: resp.totalPages,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load purchases");
    }
    setPurchasesLoading(false);
  };

  useEffect(() => {
    if (id) {
      fetchUserData();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPurchases();
    }
  }, [id, purchasesPagination.page]);

  const handleBlockToggle = async () => {
    try {
      const resp = await userBlock(id);
      if (resp?.status) {
        toast.success(resp.message || "User status updated");
        setUserData(resp.data);
        setShowBlockModal(false);
      } else {
        toast.error(resp?.message || "Failed to update user status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  const handleDeleteUser = async () => {
    try {
      const resp = await deleteUsers({ ids: [id] });
      if (resp?.status) {
        toast.success(resp.message || "User deleted successfully");
        navigate("/admin/manager/user");
      } else {
        toast.error(resp?.message || "Failed to delete user");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  const handleDeletePurchase = async () => {
    try {
      const resp = await deletePurchases({ ids: [deletePurchaseTarget] });
      if (resp?.status) {
        toast.success(resp.message || "Purchase deleted successfully");
        fetchPurchases();
        fetchUserData(); // Refresh user data to update purchase count
        setShowDeletePurchaseModal(false);
        setDeletePurchaseTarget(null);
      } else {
        toast.error(resp?.message || "Failed to delete purchase");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  if (userLoading) {
    return (
      <div className="w-full md:ml-[4.4rem] px-4 md:px-8 lg:px-10 mt-20 bg-main-bg min-h-screen pb-12">
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <span className="w-16 h-16 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
          <p className="text-main-subfont font-medium animate-pulse text-xl">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  const successfulPurchases = purchasesData.filter((p) => p.p_status === "success").length;
  const pidPurchases = purchasesData.filter((p) => p.p_type === "pid").length;
  const organicPurchases = purchasesData.filter((p) => p.p_type === "organic").length;

  return (
    <>
      <div className="w-full md:ml-[4.4rem] px-4 md:px-8 lg:px-10 mt-20 bg-main-bg min-h-screen pb-12">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
          * { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }
          .mono { font-family: 'JetBrains Mono', monospace; }
          .stat-card { background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%); }
          .stat-card-success { background: linear-gradient(135deg, #10B981 0%, #059669 100%); }
          .stat-card-warning { background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%); }
          .stat-card-danger { background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%); }
          .stat-card-purple { background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%); }
        `}</style>

        {/* HEADER */}
        <div className="mb-10">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-8">
            <div className="space-y-3">
              <button
                onClick={() => navigate("/admin/manager/user")}
                className="flex items-center gap-2 text-main-blue hover:text-main-blue/80 font-semibold text-base transition-all duration-200 mb-2"
              >
                <FaArrowLeft />
                Back to Users
              </button>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                  <FaUser className="text-2xl text-main-white" />
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-main-font tracking-tight">
                  User Details
                </h1>
              </div>
              <p className="text-main-subfont text-base lg:text-lg font-medium ml-16">
                View and manage user information
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowBlockModal(true)}
                className={`px-6 py-3 rounded-xl font-bold text-base transition-all duration-200 shadow-md flex items-center gap-2 ${userData.u_status
                  ? "bg-main-delete text-main-white hover:bg-main-delete/90"
                  : "bg-green-600 text-main-white hover:bg-green-700"
                  }`}
              >
                {userData.u_status ? <><FaBan /> Block User</> : <><FaCheckCircle /> Unblock User</>}
              </button>
              <button
                onClick={() => setShowDeleteUserModal(true)}
                className="bg-main-delete text-main-white px-6 py-3 rounded-xl font-bold text-base hover:bg-main-delete/90 transition-all duration-200 shadow-md flex items-center gap-2"
              >
                <FaTrash />
                Delete User
              </button>
            </div>
          </div>

          {/* USER INFO CARD */}
          <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-main-blue to-main-blue/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex items-center gap-6">
                {userData.u_profile && (
                  <img
                    src={userData.u_profile}
                    alt={userData.u_name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-main-white/30"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-3xl font-bold mb-2">{userData.u_name}</h2>
                  <p className="text-main-white/90 text-lg">{userData.u_email}</p>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-base ${userData.u_status ? "bg-green-500/20 text-green-100" : "bg-red-500/20 text-red-100"
                      }`}
                  >
                    {userData.u_status ? <><FaCheckCircle /> Active</> : <><FaBan /> Blocked</>}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Device</p>
                  <p className="mono text-main-font font-semibold text-base px-3 py-1.5 bg-main-bg rounded-lg inline-block">
                    {userData.u_device}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">PID</p>
                  <p className="mono text-main-font font-semibold text-base">
                    {userData.u_pid || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Total Purchases</p>
                  <p className="text-main-blue font-bold text-2xl">{userData.u_purchased || 0}</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Last Login</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(userData.u_lastLogin, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(userData.u_lastLogin, "hh:mm:ss A")}</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Registered</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(userData.createdAt, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(userData.createdAt, "hh:mm:ss A")}</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Last Updated</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(userData.updatedAt, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(userData.updatedAt, "hh:mm:ss A")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* PURCHASE HISTORY SECTION */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                <FaShoppingCart className="text-2xl text-main-white" />
              </div>
              <h2 className="text-3xl font-bold text-main-font">Purchase History</h2>
            </div>

            {/* STATS CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="stat-card text-main-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
                <p className="text-sm font-medium opacity-90 mb-1 relative z-10">Total Purchases</p>
                <p className="text-4xl font-bold relative z-10">{purchasesPagination.total}</p>
              </div>
              <div className="stat-card-success text-main-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
                <p className="text-sm font-medium opacity-90 mb-1 relative z-10">Successful</p>
                <p className="text-4xl font-bold relative z-10">{successfulPurchases}</p>
              </div>
              <div className="stat-card-warning text-main-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
                <p className="text-sm font-medium opacity-90 mb-1 relative z-10">PID Source</p>
                <p className="text-4xl font-bold relative z-10">{pidPurchases}</p>
              </div>
              <div className="stat-card-purple text-main-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
                <p className="text-sm font-medium opacity-90 mb-1 relative z-10">Organic</p>
                <p className="text-4xl font-bold relative z-10">{organicPurchases}</p>
              </div>
            </div>

            {/* PURCHASES TABLE */}
            <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-main-th border-b-2 border-main-border">
                      {["#", "Product Name", "Order ID", "Device", "PID", "Campaign", "Amount", "Type", "Status", "Created At", "Actions"].map((col) => (
                        <th key={col} className="p-5 text-left">
                          <span className="text-main-font font-bold text-sm uppercase tracking-wide">{col}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {purchasesLoading ? (
                      <tr>
                        <td colSpan="11" className="p-16">
                          <div className="flex flex-col items-center justify-center gap-4">
                            <span className="w-12 h-12 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
                            <p className="text-main-subfont font-medium animate-pulse">Loading...</p>
                          </div>
                        </td>
                      </tr>
                    ) : purchasesData.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="p-16">
                          <div className="flex flex-col items-center gap-4">
                            <div className="p-6 bg-main-bg rounded-full">
                              <FaShoppingCart className="text-6xl text-main-subfont/30" />
                            </div>
                            <div className="text-center space-y-2">
                              <p className="text-main-font font-semibold text-xl">No purchases found</p>
                              <p className="text-main-subfont text-base max-w-md">
                                This user hasn't made any purchases yet
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      purchasesData.map((row, index) => (
                        <tr
                          key={row._id}
                          className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200 cursor-pointer"
                          onClick={(e) => {
                            if (!e.target.closest("button")) {
                              setSelectedPurchase(row);
                              setShowPurchaseModal(true);
                            }
                          }}
                        >
                          <td className="p-5">
                            <span className="text-main-font font-semibold text-base mono">
                              {(purchasesPagination.page - 1) * purchasesPagination.limit + index + 1}
                            </span>
                          </td>
                          <td className="p-5">
                            <span className="text-main-font font-medium text-sm">{row.p_name || "N/A"}</span>
                          </td>
                          <td className="p-5">
                            <span className="mono text-main-font font-semibold text-sm px-3 py-1.5 bg-main-bg rounded-lg">
                              {row.p_order_id}
                            </span>
                          </td>
                          <td className="p-5">
                            <span className="mono text-main-subfont text-sm">{row.p_device}</span>
                          </td>
                          <td className="p-5">
                            <span className="mono text-main-font font-semibold text-sm">{row.p_pid || "N/A"}</span>
                          </td>
                          <td className="p-5">
                            <span className="text-main-font font-medium text-sm">{row.p_campaign || "N/A"}</span>
                          </td>
                          <td className="p-5">
                            <div className="text-center">
                              <div className="text-main-font font-bold text-base">{row.p_amount}</div>
                              <div className="text-main-subfont text-xs">{row.p_currency}</div>
                            </div>
                          </td>
                          <td className="p-5">
                            <div className="flex justify-center">
                              <span className={`px-4 py-2 font-semibold text-sm rounded-xl ${row.p_type === "pid" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"
                                }`}>
                                {row.p_type}
                              </span>
                            </div>
                          </td>
                          <td className="p-5">
                            <div className="flex justify-center">
                              <span className={`px-4 py-2 font-semibold text-sm rounded-xl ${row.p_status === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                }`}>
                                {row.p_status}
                              </span>
                            </div>
                          </td>
                          <td className="p-5">
                            <div className="text-center space-y-1">
                              <div className="text-main-font text-sm font-semibold mono">
                                {formatDateTime(row.createdAt, "DD/MM/YYYY")}
                              </div>
                              <div className="text-main-subfont text-xs mono">
                                {formatDateTime(row.createdAt, "hh:mm:ss A")}
                              </div>
                            </div>
                          </td>
                          <td className="p-5">
                            <div className="flex justify-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletePurchaseTarget(row._id);
                                  setShowDeletePurchaseModal(true);
                                }}
                                className="text-main-delete font-semibold hover:bg-main-delete/10 px-5 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 border-2 border-transparent hover:border-main-delete/20"
                              >
                                <FaTrash className="text-xs" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              {purchasesPagination.totalPages > 1 && (
                <div className="p-6 border-t-2 border-main-border flex justify-between items-center">
                  <span className="text-main-subfont text-sm font-medium">
                    Showing {(purchasesPagination.page - 1) * purchasesPagination.limit + 1} to{" "}
                    {Math.min(purchasesPagination.page * purchasesPagination.limit, purchasesPagination.total)} of{" "}
                    {purchasesPagination.total} entries
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPurchasesPagination({ ...purchasesPagination, page: purchasesPagination.page - 1 })}
                      disabled={purchasesPagination.page === 1}
                      className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                                 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                                 hover:border-main-blue transition-all duration-200"
                    >
                      Previous
                    </button>
                    <div className="flex gap-1">
                      {[...Array(purchasesPagination.totalPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setPurchasesPagination({ ...purchasesPagination, page: i + 1 })}
                          className={`w-10 h-10 rounded-xl font-bold text-sm transition-all duration-200 ${purchasesPagination.page === i + 1
                            ? "bg-main-blue text-main-white"
                            : "border-2 border-main-border hover:bg-main-blue/10"
                            }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setPurchasesPagination({ ...purchasesPagination, page: purchasesPagination.page + 1 })}
                      disabled={purchasesPagination.page === purchasesPagination.totalPages}
                      className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                                 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                                 hover:border-main-blue transition-all duration-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BLOCK/UNBLOCK CONFIRMATION MODAL */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-lg rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className={`bg-gradient-to-r p-8 text-main-white relative overflow-hidden ${userData.u_status ? "from-main-delete to-main-delete/90" : "from-green-600 to-green-700"
              }`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="p-5 bg-main-white/20 rounded-2xl backdrop-blur-sm">
                  <FaExclamationTriangle className="text-5xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">
                    {userData.u_status ? "Block User?" : "Unblock User?"}
                  </h2>
                  <p className="text-main-white/90 text-sm">
                    {userData.u_status
                      ? "This user will not be able to access the system"
                      : "This user will regain access to the system"}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="bg-main-bg/50 border-2 border-main-border rounded-2xl p-6 mb-6">
                <p className="text-main-font text-center leading-relaxed">
                  Are you sure you want to {userData.u_status ? "block" : "unblock"}{" "}
                  <span className="font-bold text-main-blue">{userData.u_name}</span>?
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 px-6 py-4 text-main-subfont font-bold hover:text-main-font 
                             hover:bg-main-bg rounded-2xl transition-all duration-200 border-2 border-main-border"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBlockToggle}
                  className={`flex-1 px-6 py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-main-white ${userData.u_status ? "bg-main-delete hover:bg-main-delete/90" : "bg-green-600 hover:bg-green-700"
                    }`}
                >
                  {userData.u_status ? <><FaBan /> Block</> : <><FaCheckCircle /> Unblock</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE USER CONFIRMATION MODAL */}
      {showDeleteUserModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-lg rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-delete to-main-delete/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="p-5 bg-main-white/20 rounded-2xl backdrop-blur-sm">
                  <FaExclamationTriangle className="text-5xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Delete User?</h2>
                  <p className="text-main-white/90 text-sm">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="bg-main-bg/50 border-2 border-main-border rounded-2xl p-6 mb-6">
                <p className="text-main-font text-center leading-relaxed">
                  You are about to permanently delete{" "}
                  <span className="font-bold text-main-blue">{userData.u_name}</span> and all associated data.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteUserModal(false)}
                  className="flex-1 px-6 py-4 text-main-subfont font-bold hover:text-main-font 
                             hover:bg-main-bg rounded-2xl transition-all duration-200 border-2 border-main-border"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="flex-1 bg-main-delete text-main-white px-6 py-4 rounded-2xl font-bold 
                             hover:bg-main-delete/90 shadow-lg hover:shadow-xl transition-all duration-300 
                             flex items-center justify-center gap-2"
                >
                  <FaTrash />
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE PURCHASE CONFIRMATION MODAL */}
      {showDeletePurchaseModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-lg rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-delete to-main-delete/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="p-5 bg-main-white/20 rounded-2xl backdrop-blur-sm">
                  <FaExclamationTriangle className="text-5xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Delete Purchase?</h2>
                  <p className="text-main-white/90 text-sm">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="bg-main-bg/50 border-2 border-main-border rounded-2xl p-6 mb-6">
                <p className="text-main-font text-center leading-relaxed">
                  Are you sure you want to delete this purchase?
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowDeletePurchaseModal(false);
                    setDeletePurchaseTarget(null);
                  }}
                  className="flex-1 px-6 py-4 text-main-subfont font-bold hover:text-main-font 
                             hover:bg-main-bg rounded-2xl transition-all duration-200 border-2 border-main-border"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePurchase}
                  className="flex-1 bg-main-delete text-main-white px-6 py-4 rounded-2xl font-bold 
                             hover:bg-main-delete/90 shadow-lg hover:shadow-xl transition-all duration-300 
                             flex items-center justify-center gap-2"
                >
                  <FaTrash />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PURCHASE DETAILS MODAL */}
      {showPurchaseModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-3xl rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-blue to-main-blue/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex justify-between items-start">
                <div className="space-y-2">
                  <h2 className="text-3xl font-bold">Purchase Details</h2>
                  <p className="text-main-white/80 text-sm">Complete purchase information</p>
                </div>
                <button
                  onClick={() => {
                    setShowPurchaseModal(false);
                    setSelectedPurchase(null);
                  }}
                  className="p-2 hover:bg-main-white/20 rounded-xl transition-all duration-200"
                >
                  <MdClose className="text-2xl" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: "Product Name", value: selectedPurchase.p_name || "N/A" },
                  { label: "Order ID", value: selectedPurchase.p_order_id, mono: true },
                  { label: "Device", value: selectedPurchase.p_device, mono: true },
                  { label: "PID", value: selectedPurchase.p_pid || "N/A", mono: true },
                  { label: "Campaign", value: selectedPurchase.p_campaign || "N/A" },
                ].map(({ label, value, mono }) => (
                  <div key={label}>
                    <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">{label}</p>
                    <p className={`text-main-font font-semibold text-lg ${mono ? "mono" : ""}`}>{value}</p>
                  </div>
                ))}

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Amount</p>
                  <p className="text-main-font font-bold text-2xl">{selectedPurchase.p_amount}</p>
                  <p className="text-main-subfont text-sm">{selectedPurchase.p_currency}</p>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Status</p>
                  <span className={`inline-flex px-4 py-2 font-semibold text-sm rounded-xl ${selectedPurchase.p_status === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}>
                    {selectedPurchase.p_status}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Type</p>
                  <span className={`inline-flex px-4 py-2 font-semibold text-sm rounded-xl ${selectedPurchase.p_type === "pid" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"
                    }`}>
                    {selectedPurchase.p_type}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Created At</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(selectedPurchase.createdAt, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(selectedPurchase.createdAt, "hh:mm:ss A")}</p>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Updated At</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(selectedPurchase.updatedAt, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(selectedPurchase.updatedAt, "hh:mm:ss A")}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 p-8 bg-main-bg/30 border-t-2 border-main-border">
              <button
                onClick={() => {
                  setShowPurchaseModal(false);
                  setSelectedPurchase(null);
                }}
                className="bg-main-blue text-main-white px-10 py-3.5 rounded-2xl font-bold 
                           hover:bg-main-blue/90 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserInfo;